import React, { useCallback, useEffect, useState } from "react";
import { Box, CircularProgress, Modal, Typography } from "@mui/material";
import { styled } from "@mui/system";
import { Disclosure, DisclosureStatus, SupportedLanguage } from "./disclosure.types";
import { DisclosureContentModal } from "./DisclosureContentModal";
import { DisclosureListModal } from "./DisclosureListModal";
import { AcceptButton } from "./AcceptButton";
import { DEFAULT_LANGUAGE } from "./disclosures.config";
import { usePublicDisclosureSet } from "./usePublicDisclosureSet";

// Mirror DisclosureListModal's centered white card so loading/error states match the flow.
const StatusContainer = styled(Box)({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  display: "flex",
  padding: "40px",
  flexDirection: "column",
  alignItems: "center",
  gap: "32px",
  borderRadius: "28px",
  background: "#FFF",
  boxShadow: "0 4px 20px 0 rgba(173, 192, 203, 0.30)",
  outline: "none",
  width: "95%",
  maxWidth: "518px",
  textAlign: "center",
});

const STATUS_TEXT_SX = {
  fontFamily: "Roobert",
  fontSize: "18px",
  fontWeight: 500,
  lineHeight: "26px",
  color: "#002035",
} as const;

const DisclosureStatusModal: React.FC<{
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
}> = ({ open, onClose, children }) => (
  <Modal open={open} onClose={onClose}>
    <StatusContainer>{children}</StatusContainer>
  </Modal>
);

interface DisclosureFlowProps {
  /** ISO 3166-2 region code of the signup state (e.g. "CA"); undefined shows the ToB-only set */
  region?: string;
  /** Whether the flow is currently open */
  isOpen: boolean;
  /** Callback when the flow is closed */
  onClose: () => void;
  /** Callback when all disclosures are accepted and user clicks continue */
  onComplete: (acceptedDisclosures: Record<string, DisclosureStatus>) => void;
  /** Initial accepted disclosures state (for resuming flows) */
  initialAcceptedState?: Record<string, DisclosureStatus>;
  /** Initial selected language */
  initialLanguage?: SupportedLanguage;
  /** Auto-trigger mode: automatically opens when there are pending disclosures */
  autoTrigger?: boolean;
}

export const DisclosureFlow: React.FC<DisclosureFlowProps> = ({
  region,
  isOpen,
  onClose,
  onComplete,
  initialAcceptedState = {},
  initialLanguage = DEFAULT_LANGUAGE,
  autoTrigger = false,
}) => {
  const [acceptedDisclosures, setAcceptedDisclosures] =
    useState<Record<string, DisclosureStatus>>(initialAcceptedState);
  const [selectedDisclosure, setSelectedDisclosure] = useState<Disclosure | null>(null);
  const [selectedLanguage, setSelectedLanguage] = useState<SupportedLanguage>(initialLanguage);
  const [isContentModalOpen, setIsContentModalOpen] = useState(false);
  const [isListModalOpen, setIsListModalOpen] = useState(false);

  const {
    disclosureSet,
    isLoading: isDisclosureSetLoading,
    isError: isDisclosureSetError,
    refetch: refetchDisclosureSet,
  } = usePublicDisclosureSet(region);

  // Handle auto-trigger mode
  useEffect(() => {
    if (autoTrigger && isOpen && disclosureSet) {
      const hasPendingDisclosures = disclosureSet.disclosures.some(
        (d) => acceptedDisclosures[d.id] !== "accepted",
      );
      if (hasPendingDisclosures) {
        setIsListModalOpen(true);
      }
    }
  }, [autoTrigger, isOpen, disclosureSet, acceptedDisclosures]);

  // Sync list modal open state with parent
  useEffect(() => {
    if (isOpen && !autoTrigger) {
      setIsListModalOpen(true);
    }
  }, [isOpen, autoTrigger]);

  // Handle closing the list modal
  const handleListModalClose = useCallback(() => {
    setIsListModalOpen(false);
    onClose();
  }, [onClose]);

  // Handle clicking on a disclosure to view its content
  const handleDisclosureClick = useCallback((disclosure: Disclosure) => {
    setSelectedDisclosure(disclosure);
    setIsContentModalOpen(true);
  }, []);

  // Handle accepting a disclosure
  const handleAccept = useCallback((disclosureId: string) => {
    setAcceptedDisclosures((prev) => ({
      ...prev,
      [disclosureId]: "accepted",
    }));
  }, []);

  // Handle closing the content modal
  const handleContentModalClose = useCallback(() => {
    setIsContentModalOpen(false);
    setSelectedDisclosure(null);
  }, []);

  // Handle language change
  const handleLanguageChange = useCallback((language: SupportedLanguage) => {
    setSelectedLanguage(language);
  }, []);

  // Handle continue button click
  const handleContinue = useCallback(() => {
    onComplete(acceptedDisclosures);
    setIsListModalOpen(false);
    onClose();
  }, [acceptedDisclosures, onComplete, onClose]);

  // Check if all disclosures are accepted
  const isComplete =
    disclosureSet?.disclosures.every((d) => acceptedDisclosures[d.id] === "accepted") ?? false;

  if (!disclosureSet) {
    // Nothing to show until the flow is opened by the parent.
    if (!isOpen) return null;

    // Still resolving the legal documents from the public endpoint.
    if (isDisclosureSetLoading) {
      return (
        <DisclosureStatusModal open onClose={onClose}>
          <CircularProgress sx={{ color: "#002035" }} />
          <Typography sx={STATUS_TEXT_SX}>Loading... Please wait.</Typography>
        </DisclosureStatusModal>
      );
    }

    // Fetch failed (or the set is otherwise unavailable) — give the user a way to recover
    // instead of stranding them on an empty disclosure stage.
    console.warn(`Disclosure set not available: ${region ?? "default"}`);
    return (
      <DisclosureStatusModal open onClose={onClose}>
        <Typography sx={STATUS_TEXT_SX}>
          {isDisclosureSetError
            ? "We couldn't load the required disclosures. Please check your connection and try again."
            : "The required disclosures are currently unavailable. Please try again."}
        </Typography>
        <AcceptButton onClick={refetchDisclosureSet}>Try again</AcceptButton>
      </DisclosureStatusModal>
    );
  }

  return (
    <>
      <DisclosureListModal
        open={isListModalOpen}
        onClose={handleListModalClose}
        disclosureSet={disclosureSet}
        acceptedDisclosures={acceptedDisclosures}
        onDisclosureClick={handleDisclosureClick}
        onContinue={handleContinue}
        isComplete={isComplete}
      />

      <DisclosureContentModal
        open={isContentModalOpen}
        onClose={handleContentModalClose}
        disclosure={selectedDisclosure}
        selectedLanguage={selectedLanguage}
        onLanguageChange={handleLanguageChange}
        onAccept={handleAccept}
      />
    </>
  );
};

export default DisclosureFlow;
